import z from 'zod/v4';
import { zodInstant, zodPlainDate } from '~/schemas/custom-schemas';
import { asyncFileUploadErrorSchema } from '~/schemas/data-import';

export const jobOperationTabEnum = z.enum(['COMPLETED', 'QUEUED']);
export const jobOperationStatusEnum = z.enum(['QUEUED', 'IN_PROGRESS', 'COMPLETED', 'FAILED']);

// Filter only pseudo statuses resolved into has_warnings/id query params before any request.
// These should never be used directly in any request or response payloads.
export const jobOperationPseudoStatusEnum = z.enum(['WITH_WARNINGS', 'WITH_UNREAD_WARNINGS']);
export const jobOperationStatusFilterEnum = z.enum([
  ...jobOperationStatusEnum.options,
  ...jobOperationPseudoStatusEnum.options,
]);

export const jobOperationUploadedByEnum = z.enum(['UI', 'API']);
// SYNC: an edit that was logged as a completed operation and was never queued.
export const jobOperationExecutionTypeEnum = z.enum(['ASYNC', 'SYNC']);
export const sortDirectionEnum = z.enum(['DESC', 'ASC']);

const jobOperation = z.object({
  id: z.uuid(),
  queued_at: zodInstant,
  completed_at: zodInstant.nullable(),
  filename: z.string().nullable(),
  operation_type: z.string(),
  status: jobOperationStatusEnum,
  uploaded_by: jobOperationUploadedByEnum,
  has_warnings: z.boolean().default(false),
  execution_type: jobOperationExecutionTypeEnum.default(jobOperationExecutionTypeEnum.enum.ASYNC),
});

const jobOperationDetail = jobOperation.extend({
  input_details: z.record(z.string(), z.any()).nullable(),
  output_details: z.record(z.string(), z.any()).nullable(),
  queue_position: z.number().nullable(),
});

export const jobOperationPollResponseSchema = z.object({
  events: z.array(jobOperation),
  warning_event_ids: z.array(z.uuid()).default([]),
  next_after: zodInstant,
});

export const jobOperationRequestQueryParamsSchema = z.object({
  after: zodInstant.optional(),
  before: zodInstant.optional(),
  limit: z.number().int().positive().optional(),
  queued_at: zodPlainDate.optional(),
  completed_at: zodPlainDate.optional(),
  id: z.array(z.uuid()).optional(),
  operation_type: z.array(z.string()).optional(),
  status: z.array(jobOperationStatusEnum).optional(),
  uploaded_by: z.array(jobOperationUploadedByEnum).optional(),
  has_warnings: z.boolean().optional(),
  sort_direction: sortDirectionEnum.optional(),
});

export const jobOperationResponseSchema = z.object({
  events: z.array(jobOperationDetail),
  next_before: zodInstant.nullable(),
  next_after: zodInstant.nullable(),
});

export const jobOperationDetailQueryParams = z.object({
  error_limit: z.number().positive().optional(),
  warning_limit: z.number().positive().optional(),
});

export const jobOperationDetailResponseSchema = z.object({
  operation_type: z.string(),
  queued_at: zodInstant,
  completed_at: zodInstant.nullable(),
  status: jobOperationStatusEnum,
  filename: z.string().nullable(),
  execution_type: jobOperationExecutionTypeEnum.default(jobOperationExecutionTypeEnum.enum.ASYNC),
  input_details: z.record(z.string(), z.any()).nullable(),
  output_details: z.record(z.string(), z.any()).nullable(),
  warnings: z.array(z.record(z.string(), z.any())).nullable(),
  errors: z.array(asyncFileUploadErrorSchema).nullable(),
});

export const jobOperationFilterValuesResponseSchema = z.object({
  operation_type: z.array(z.string()),
});

export const asyncOperationResponseSchema = z.object({ id: z.uuid() });

export type JobOperationTab = z.infer<typeof jobOperationTabEnum>;
export type JobOperationStatus = z.infer<typeof jobOperationStatusEnum>;
export type JobOperationStatusFilter = z.infer<typeof jobOperationStatusFilterEnum>;
export type JobOperationUploadedBy = z.infer<typeof jobOperationUploadedByEnum>;
export type SortDirection = z.infer<typeof sortDirectionEnum>;

export type JobOperation = z.infer<typeof jobOperation>;
export type JobOperationDetail = z.infer<typeof jobOperationDetail>;

export type JobOperationPollResponse = z.infer<typeof jobOperationPollResponseSchema>;
export type JobOperationRequestFilters = z.infer<typeof jobOperationRequestQueryParamsSchema>;
export type JobOperationResponse = z.infer<typeof jobOperationResponseSchema>;

export type JobOperationDetailQueryParams = z.infer<typeof jobOperationDetailQueryParams>;
export type JobOperationDetailResponse = z.infer<typeof jobOperationDetailResponseSchema>;

export type JobOperationFilterValuesResponse = z.infer<typeof jobOperationFilterValuesResponseSchema>;
